
import { SismoazurEventParameter, StationObject } from '@/types'
import * as utils from '@/utils'
import L from 'leaflet'
import Vue from 'vue'

export default Vue.extend({
  props: {
    localArea: {
      type: Boolean,
      default: true
    },
    disclaimer: {
      type: Boolean,
      default: false
    },
    fm: {
      type: Boolean,
      default: false
    },
    drawEventsOnMount: {
      type: Boolean,
      default: false
    },
    resize: {
      type: Boolean,
      default: false
    },
    events: {
      type: Array as () => SismoazurEventParameter[],
      default: () => []
    },
    focusEvent: {
      type: Object as () => SismoazurEventParameter
    },
    station: {
      type: Object as () => StationObject
    }
  },

  data () {
    return {
      map: null as L.Map | null,
      eventMarker: {} as Record<string, any>,
      focusEventLayers: [] as L.Layer[],
      magScale: utils.SCALE_MAG_COLOR,
      legendPanel: this.$vuetify.breakpoint.mobile ? [] : [0],
      legendMore: [] as number[]
    }
  },
  computed: {
    catalogBounds (): L.LatLngTuple[] {
      const eventList = this.events as SismoazurEventParameter[]
      return eventList.map((x: SismoazurEventParameter) => [x._po.latitude.value, x._po.longitude.value])
    }
  },
  mounted () {
    this.initMap()
    if (this.drawEventsOnMount) {
      this.drawEvents()
    }
    if (this.focusEvent != null) {
      this.doFocusEvent(this.focusEvent)
    }
  },
  watch: {
    '$store.state.position': function (value) {
      if (value != null) {
        this.displayCurrentPosition()
      }
    },
    events: function () {
      this.drawEvents()
    },
    focusEvent: function (value) {
      if (value != null) {
        this.clearFocusEvent()
        this.doFocusEvent(value)
      } else {
        this.clearFocusEvent()
        if (this.map != null) {
          if (this.localArea) {
            this.map.fitBounds(utils.LOCAL_AREA_SHAPE)
          } else {
            this.map.fitBounds(this.catalogBounds)
          }
        }
      }
    }
  },
  methods: {
    initMap () {
      const mapCanvas = this.$refs.mapCanvas
      const legend = this.$refs.legend
      const disclaimer = this.$refs.disclaimer as HTMLElement
      if (mapCanvas == null || legend == null) {
        return
      }
      const LegendControl = L.Control.extend({ onAdd: function () { return legend } })
      const DisclaimerControl = L.Control.extend({ onAdd: function () { return disclaimer } })
      const disclaimerInstance = this.disclaimer ? new DisclaimerControl({ position: 'bottomleft' }) : null
      if (disclaimerInstance == null) {
        disclaimer.style.display = 'none'
      }
      const map = utils.createMap(mapCanvas, {
        scrollWheelZoom: false,
        zoomControl: false,
        dragging: !this.$vuetify.breakpoint.mobile,
        trackResize: this.resize
      }, disclaimerInstance, new LegendControl({ position: 'topleft' }))
      map.on('click', (ev) => {
        this.$emit('mapClick')
        // if (this.$store.state.focusEventId != null) {
        //   this.$store.dispatch('focusEvent', this.$store.state.focusEventId)
        // }
      })
      L.control.zoom({ position: 'topright' }).addTo(map)

      if (this.localArea) {
        L.polygon([[[90, -180], [90, 180], [-90, 180], [-90, -180]], utils.LOCAL_AREA_SHAPE as L.LatLngExpression[]], {
          weight: 1,
          color: '#858585',
          dashArray: [3, 3],
          fillColor: '#000',
          fillOpacity: 0.1
        }).addTo(map)

        map.fitBounds(utils.LOCAL_AREA_SHAPE)
      }

      this.map = map

      if (this.$store.state.position != null) {
        this.displayCurrentPosition()
      }
    },

    displayCurrentPosition () {
      if (this.map != null) {
        const color = 'DodgerBlue'
        L.marker([
          this.$store.state.position.coords.latitude,
          this.$store.state.position.coords.longitude
        ], {
          icon: L.divIcon({
            iconSize: [22, 22],
            iconAnchor: [11, 11],
            className: '',
            html: `\
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22">
              <circle cx="11" cy="11" r="7" fill="rgba(255, 255, 255, 0.7)"></circle>
              <circle cx="11" cy="11" r="7" fill="none" stroke="${color}" stroke-width="2"></circle>
              <circle cx="11" cy="11" r="3" fill="${color}" stroke="none"></circle>
              <path d="M 11 0 l 0 4 M 11 18 l 0 4 M 0 11 l 4 0 M 18 11 l 4 0" stroke="${color}" stroke-width="2"></path>
            </svg>`
          })
        }).addTo(this.map)
      }
    },

    getEventMarker (event: SismoazurEventParameter) {
      let marker = null
      const pos: L.LatLngExpression = [event._po.latitude.value, event._po.longitude.value]
      if (this.fm) {
        const size = event._size
        marker = L.marker(pos, {
          icon: L.icon({
            // iconUrl: root+'/resource/beachball?strike='+fm.strike+'&dip='+fm.dip+'&rake='+fm.rake+'&color='+color,
            iconUrl: event._fm._beachball as string,
            iconSize: [size, size],
            popupAnchor: [0, -size / 2]
          })
        })
        const popupContent = document.createElement('div')
        popupContent.innerHTML = `\
          <table class="event_map__fm_popup">
            <tr><th>Date and time (UTC)</th><td>${event._po.time._pretty}</td></tr>
            <tr><th>Magnitude</th><td>${event._fm._mag?.mag.value.toFixed(1)} ${event._fm._mag?.type}(${event._fm._mag?.method_id})</td></tr>
            <tr><th>Lat | Lon | Depth</th><td>${event._po.latitude._pretty} | ${event._po.longitude._pretty} | ${event._fm._extra?.depth} km</td></tr>
            <tr><th>Strike | Dip | Rake</th><td>${event._fm.nodal_planes?.nodal_plane1?.strike.value} | ${event._fm.nodal_planes?.nodal_plane1?.dip.value} | ${event._fm.nodal_planes?.nodal_plane1?.rake.value}</td></tr>
            <tr><th>Index | Quality | RMS</th><td>${event._fm._extra?.index_of_confidence}% | ${event._fm._extra?.quality || '-'} | ${event._fm._extra?.rms}</td></tr>
          </table>
          <a href="#" class="s-modal-fm">Waveforms and more</a>
          <div class="small-font text-right">ID: ${event.public_id}</div>`
        popupContent.querySelector('.s-modal-fm')?.addEventListener('click', ev => {
          ev.preventDefault()
          this.$emit('fmModal', event)
        })
        marker.bindPopup(popupContent)
      } else if (event.type === 'earthquake') {
        marker = L.marker(pos, {
          pane: 'markerPane',
          icon: L.divIcon({
            iconSize: [2 * (event._size + 1), 2 * (event._size + 1)],
            className: '',
            html: `\
            <svg xmlns="http://www.w3.org/2000/svg" width="${2 * (event._size + 1)}" height="${2 * (event._size + 1)}">
              <circle cx="${event._size + 1}" cy="${event._size + 1}" r="${event._size}" stroke="black" stroke-width="1" fill="${event._color}" fill-opacity="1"/>
            </svg>`
          })
        })
      } else if (event.type === 'explosion' || event.type === 'quarry blast') {
        marker = L.marker(pos, {
          pane: 'markerPane',
          opacity: 0.75,
          icon: L.divIcon({
            iconSize: [10 * event._size, 10 * event._size],
            className: '',
            html: `\
              <svg xmlns="http://www.w3.org/2000/svg" width="${10 * event._size}" height="${10 * event._size}">
                <path d="M 2.1 0.5 L 4.2 3.2 L 4.7 2.6 L 5.3 3.2 L 6.8 1.1 L 6.3 3.2 L 6.8 3.2 L 6.3 4.2 L 8.9 2.6 L 6.8 5.3 L 7.9 5.3 L 7.4 6.3 L 8.9 8.9 L 6.3 6.8 L 5.8 8.9 L 5.3 7.4 L 4.7 7.9 L 4.2 6.8 L 2.1 9.5 L 3.7 6.8 L 2.6 6.8 L 3.2 6.3 L 0.5 6.3 L 3.2 5.3 L 2.6 4.7 L 3.2 4.2 L 2.6 3.7 L 3.2 3.7 Z" fill="${event._color}" stroke="black" stroke-width="${1 / (event._size * 1.5)}" transform="scale(${event._size})"/>
              </svg>`
          })
        })
      } else if (event.type === 'other event') {
        marker = L.marker(pos, {
          pane: 'markerPane',
          icon: L.divIcon({
            iconSize: [event._size + 2, event._size + 2],
            className: '',
            html: `\
              <svg xmlns="http://www.w3.org/2000/svg" width="${event._size + 2}" height="${event._size + 2}">
                <rect x="1" y="1" width="${event._size}" height="${event._size}" stroke-width="1" stroke="black" fill="${event._color}" fill-opacity="1"/>
              </svg>`
          })
        })
      } else {
        marker = L.marker(pos, {
          pane: 'markerPane',
          icon: L.divIcon({
            iconSize: [event._size + 2, event._size + 2],
            className: '',
            html: `\
              <svg xmlns="http://www.w3.org/2000/svg" width="${event._size + 2}" height="${event._size + 2}">
                <rect x="1" y="1" width="${event._size}" height="${event._size}" stroke-dasharray="2,2" stroke-width="1" stroke="black" fill="${event._color}" fill-opacity="1"/>
              </svg>`
          })
        })
      }
      if (this.station != null) {
        const popupContent = document.createElement('div')
        popupContent.innerHTML = `\
          <table class="event_map__fm_popup">
            <tr><th>Date and time (UTC)</th><td>${event._po.time._pretty}</td></tr>
            <tr><th>Magnitude</th><td>${event._pm!.mag.value.toFixed(1)}</td></tr>
            <tr><th>Lat | Lon | Depth</th><td>${event._po.latitude._pretty} | ${event._po.longitude._pretty} | ${event._po.depth._pretty}</td></tr>
          </table>`
        marker.bindPopup(popupContent)
      }
      marker.on('click', () => {
        this.$emit('eventClick', event)
        // this.$store.dispatch('focusEvent', event.public_id)
      })
      return marker
    },

    drawEvents () {
      if (this.map == null) {
        return
      }
      const eventList = this.events as SismoazurEventParameter[]
      if (Object.keys(this.eventMarker).length > 0) {
        this.clearFocusEvent()
        for (const l of Object.values(this.eventMarker)) {
          l.remove()
        }
        this.eventMarker = {}
      }
      if (this.station != null) {
        for (const event of eventList) {
          const line = L.polyline([this.station._pos, event._po._pos], {
            color: 'gray',
            weight: 1
          })
          this.focusEventLayers.push(line)
          line.addTo(this.map)
        }
      }
      for (const [i, event] of eventList.entries()) {
        const marker = this.getEventMarker(event)
        this.eventMarker[event.public_id] = marker
        marker.setZIndexOffset(i)
        marker.addTo(this.map)
      }
      if (this.station != null) {
        const stationMarker = this.getStationMarker(this.station._pos)
        this.focusEventLayers.push(stationMarker)
        stationMarker.addTo(this.map)
      }
      if (!this.localArea) {
        if (this.catalogBounds.length > 0) {
          this.map.fitBounds(this.catalogBounds)
        }
      }
    },

    getStationMarker (pos: L.LatLngExpression) {
      return L.marker(pos, {
        icon: L.divIcon({
          iconSize: [10, 10],
          iconAnchor: [5, 10],
          className: '',
          html: `\
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10">
              <path d="M 1 8 L 5 1 L 9 8 Z" fill="white" stroke="black"/>
            </svg>`
        })
      })
    },

    clearFocusEvent () {
      for (const layer of this.focusEventLayers) {
        layer.remove()
      }
      for (const marker of Object.values(this.eventMarker)) {
        if (marker.setOpacity == null) {
          marker.setStyle({ opacity: 1, fillOpacity: 1 })
        } else {
          marker.setOpacity(1)
        }
      }
      this.focusEventLayers = []
      if (this.fm && this.map != null) {
        this.map.closePopup()
      }
    },

    doFocusEvent (event: SismoazurEventParameter) {
      if (this.map == null) {
        return
      }
      // const event = this.$store.getters.eventById(eventid)
      const eventPos: L.LatLngExpression = [event._po.latitude.value, event._po.longitude.value]
      for (const [id, marker] of Object.entries(this.eventMarker)) {
        const opacity = id === event.public_id ? 1 : 0.3
        if (marker.setOpacity == null) {
          marker.setStyle({ opacity, fillOpacity: opacity })
        } else {
          marker.setOpacity(opacity)
        }
      }
      if (this.fm) {
        this.eventMarker[event.public_id].openPopup()
      }
      if (event._po.arrival == null) {
        return
      }
      const bounds: L.LatLngExpression[] = [eventPos]
      for (const arrival of event._po.arrival) {
        if (arrival.time_weight !== 1) {
          continue
        }
        const wfid = arrival._pick!.waveform_id
        const stationInv = this.$store.getters.station(wfid.network_code, wfid.station_code)
        if (stationInv == null) {
          console.warn(`skip station ${wfid.network_code}_${wfid.station_code} in event map: not found in inventory`)
          continue
        }
        const stationLatLng: L.LatLngExpression = [stationInv.lat, stationInv.lon]
        bounds.push(stationLatLng)
        const line = L.polyline([eventPos, stationLatLng], { color: 'black', weight: 1, opacity: 0.4 })
        const station = this.getStationMarker(stationLatLng)
        // line.addTo(this.map).bringToBack()
        line.addTo(this.map)
        station.addTo(this.map)
        this.focusEventLayers.push(line)
        this.focusEventLayers.push(station)
        station.bindPopup(`${wfid.network_code}_${wfid.station_code}`)
      }
      this.map.fitBounds(bounds as L.LatLngBoundsExpression)
    }
  }
})
